/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createRef, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Coordinate,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatDate } from '../../../../helpers/date-formatters';
import { formatNumber } from '../../../../helpers/number-formatters';
import { GraphPoint } from '../../../../logic/graphql/types/types';
import { ActiveDot, ChartContainer, CustomTooltip, Dot } from './components';
import { calculateGraphRangeData, calculateXAxisProperties } from './helpers';
import { useSelector } from 'react-redux';
import Presentation from '../../../../logic/redux/presentation';

const MyAreaChart = (props: { data: GraphPoint[] }): JSX.Element => {
  const screenSize = useSelector(Presentation.select.selectScreenSize);

  const calculatedXAxisProperties = calculateXAxisProperties(screenSize, props.data.length);

  const [tooltipCoordinate, setTooltipCoordinate] = useState<Coordinate | undefined>(undefined);
  const areaRef = createRef<Area>();
  const { rangeMin, rangeMax, ticks } = calculateGraphRangeData(props.data);

  const onChartMouseMove = (chart: any): void => {
    if (chart && chart.isTooltipActive) {
      const chartPoints = areaRef?.current?.props?.points;
      const point = (chartPoints || [])[chart.activeTooltipIndex];
      if (point && point.x && point.y && point !== tooltipCoordinate) {
        setTooltipCoordinate({ x: point.x - 0, y: point.y - 51 });
      }
    }
  };

  const onChartMouseLeave = (): void => {
    setTooltipCoordinate(undefined);
  };

  const renderChart = (): JSX.Element => {
    return (
      <ResponsiveContainer>
        <AreaChart
          data={props.data}
          onMouseMove={onChartMouseMove}
          onMouseLeave={onChartMouseLeave}
        >
          <defs>
            <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#000" stopOpacity={1} />
              <stop offset="100%" stopColor="#202020" stopOpacity={1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeWidth={1} stroke="#2E2E2E" vertical={false} />
          <XAxis
            dataKey="time"
            stroke="#fff"
            axisLine={false}
            tickLine={false}
            tickFormatter={(t) => formatDate(t)}
            {...calculatedXAxisProperties}
          />
          <YAxis
            stroke="#fff"
            tickFormatter={(num) => formatNumber(Math.floor(num / 1000))}
            dx={-10}
            domain={[rangeMin, rangeMax]}
            ticks={ticks}
            axisLine={false}
            tickLine={false}
            unit={'e'}
            type="number"
            width={30}
          />
          <Tooltip
            isAnimationActive={false}
            content={CustomTooltip}
            position={tooltipCoordinate}
            cursor={false}
          />
          <Area
            ref={areaRef}
            isAnimationActive={false}
            type="linear"
            dataKey="value"
            stroke="#FAD28C"
            strokeWidth={3}
            fill="url(#colorUv)"
            dot={props.data.length > 32 ? false : Dot}
            activeDot={ActiveDot}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  return <ChartContainer>{renderChart()}</ChartContainer>;
};

export default MyAreaChart;
